// Production-aligned navbar: platform feeds + About/How to/Feedback/Privacy, theme cycle, rate app.
import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Moon,
  Sun,
  Monitor,
  Plus,
  Menu,
  X,
  Info,
  BookOpen,
  MessageSquare,
  Shield,
  Star,
} from 'lucide-react';
import { FaYoutube, FaInstagram, FaTwitter, FaSoundcloud } from 'react-icons/fa';
import type { ThemePreference } from '../lib/theme';
import { openRateApp } from '../lib/rateApp';

interface NavbarProps {
  onPostClick: () => void;
  isDarkMode?: boolean;
  themePreference: ThemePreference;
  onCycleTheme: () => void;
}

const navItems = [
  { to: '/', label: 'Home', icon: Home, hoverClass: 'group-hover:text-pink-400' },
  { to: '/youtube', label: 'YouTube', icon: FaYoutube, hoverClass: 'group-hover:text-red-500' },
  { to: '/instagram', label: 'Instagram', icon: FaInstagram, hoverClass: 'group-hover:text-pink-500' },
  { to: '/twitter', label: 'X', icon: FaTwitter, hoverClass: 'group-hover:text-sky-400' },
  { to: '/soundcloud', label: 'SoundCloud', icon: FaSoundcloud, hoverClass: 'group-hover:text-orange-400' },
  { to: '/about', label: 'About', icon: Info, hoverClass: 'group-hover:text-pink-400' },
  { to: '/how-to-use', label: 'How to', icon: BookOpen, hoverClass: 'group-hover:text-pink-400' },
  { to: '/feedback', label: 'Feedback', icon: MessageSquare, hoverClass: 'group-hover:text-pink-400' },
  { to: '/privacy', label: 'Privacy', icon: Shield, hoverClass: 'group-hover:text-pink-400' },
] as const;

function themeLabel(preference: ThemePreference): string {
  if (preference === 'system') return 'System theme';
  if (preference === 'light') return 'Light mode';
  return 'Dark mode';
}

function ThemeIcon({ preference }: { preference: ThemePreference }) {
  if (preference === 'system') return <Monitor size={16} />;
  if (preference === 'light') return <Sun size={16} />;
  return <Moon size={16} />;
}

export default function Navbar({
  onPostClick,
  themePreference,
  onCycleTheme,
}: NavbarProps) {
  const [menuKey, setMenuKey] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isMenuOpen = menuKey === location.pathname;

  useEffect(() => {
    const updateScrolled = () => setIsScrolled(window.scrollY > 24);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuKey(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const closeMenu = () => setMenuKey(null);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 pt-4 sm:pt-5">
        <div
          className={`max-w-7xl mx-auto transition-all duration-500 ease-out ${
            isScrolled
              ? 'bg-white/70 dark:bg-slate-950/70 backdrop-blur-2xl border border-slate-200/60 dark:border-white/10 rounded-2xl shadow-lg shadow-slate-900/5 dark:shadow-black/30'
              : 'bg-transparent border border-transparent'
          }`}
        >
          <div className="flex items-center justify-between gap-3 h-14 sm:h-16 px-4 sm:px-5">
            <nav className="hidden lg:flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2 bg-slate-100/80 dark:bg-white/5 backdrop-blur-md rounded-full p-1 border border-slate-200/70 dark:border-white/10">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-1.5 px-2.5 xl:px-3.5 py-2 rounded-full text-xs font-medium tracking-wide transition-all duration-200 ${
                      isActive
                        ? 'text-slate-900 dark:text-white'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute inset-0 rounded-full bg-white dark:bg-white/10 shadow-sm border border-slate-200/80 dark:border-white/10" />
                      )}
                      <item.icon
                        size={14}
                        className={`relative z-10 transition-colors ${item.hoverClass} ${
                          isActive ? 'text-pink-500' : ''
                        }`}
                      />
                      <span className="relative z-10 whitespace-nowrap">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              <button
                onClick={onCycleTheme}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/80 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-sm text-slate-700 dark:text-white hover:bg-white dark:hover:bg-white/10 transition-all"
                aria-label={`${themeLabel(themePreference)}. Tap to change theme.`}
                title={`${themeLabel(themePreference)} — tap to cycle`}
              >
                <ThemeIcon preference={themePreference} />
              </button>

              <button
                onClick={onPostClick}
                className="flex items-center justify-center gap-2 bg-linear-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white rounded-full font-medium transition-all shadow-[0_0_20px_rgba(219,39,119,0.25)] hover:shadow-[0_0_28px_rgba(219,39,119,0.45)] active:scale-95 text-sm h-9 w-9 md:h-auto md:w-auto md:px-5 md:py-2.5"
                aria-label="Share content"
              >
                <Plus size={16} />
                <span className="hidden xl:inline">Share</span>
              </button>

              <button
                onClick={() =>
                  setMenuKey((key) => (key === location.pathname ? null : location.pathname))
                }
                className="lg:hidden flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/80 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-sm text-slate-700 dark:text-white hover:bg-white dark:hover:bg-white/10 transition-all"
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-md"
          onClick={closeMenu}
          aria-hidden="true"
        />

        <div
          className={`absolute top-0 right-0 h-full w-full max-w-sm bg-white/95 dark:bg-slate-950/95 backdrop-blur-2xl border-l border-slate-200/80 dark:border-white/10 shadow-2xl transition-transform duration-300 ease-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full p-6 pt-24">
            <div className="mb-6 flex-1 overflow-y-auto">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-500 mb-4">
                Navigate
              </p>
              <nav className="flex flex-col gap-1">
                {navItems.map((item, index) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    style={{ transitionDelay: isMenuOpen ? `${index * 40}ms` : '0ms' }}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-4 py-3.5 rounded-2xl text-base font-medium transition-all duration-300 ${
                        isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                      } ${
                        isActive
                          ? 'bg-pink-50 dark:bg-pink-500/10 text-pink-600 dark:text-pink-300'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`
                    }
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/5">
                      <item.icon size={18} className={item.hoverClass} />
                    </span>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>

            <div className="border-t border-slate-200 dark:border-white/10 pt-4 space-y-2">
              <button
                type="button"
                onClick={() => {
                  openRateApp();
                  closeMenu();
                }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400">
                  <Star size={18} />
                </span>
                Rate this app
              </button>
              <p className="px-4 text-xs text-slate-500 dark:text-slate-500">
                Theme: {themeLabel(themePreference)} (tap sun/moon/monitor icon)
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
